import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { AnswerOption, QuestionBoxComponent } from '@shared/components/question-box/question-box.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { LegalChecksDataComponent } from '../legal-checks-data/legal-checks-data.component';
import { ContactDataComponent } from '../contact-data/contact-data.component';
import { AddressDataComponent } from '../address-data/address-data.component';
import { DataBaseComponent } from '@shared/directives/data-base.component';
import { CustomerFormModel } from '@shared/models/forms/customer-form.model';
import { ButtonBusyDirective } from '@shared/directives/button-busy.directive';
import { UrlParamProviderService } from '@shared/services/url-param-provider.service';
import { Gender } from '@backend/models';

@Component({
  selector: 'app-customer-data',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    QuestionBoxComponent,
    InputNumberModule,
    LegalChecksDataComponent,
    ContactDataComponent,
    AddressDataComponent,
    ButtonBusyDirective
  ],
  templateUrl: './customer-data.component.html',
  styleUrl: './customer-data.component.scss'
})
export class CustomerDataComponent extends DataBaseComponent implements OnInit {

  urlParamProviderService = inject(UrlParamProviderService);

  @Input() form!: FormGroup<CustomerFormModel>;

  @Output() submitted = new EventEmitter<void>();

  maxBirthdate!: string;
  sendingRequest: boolean = false;
  genderAnswerOptions: AnswerOption<Gender>[] = [
    { title: 'shared.maleGender', id: Gender.Male, value: Gender.Male },
    { title: 'shared.femaleGender', id: Gender.Female, value: Gender.Female },
    { title: 'shared.undefinedGender', id: Gender.Divers, value: Gender.Divers }
  ];

  ngOnInit(): void {
    let date = new Date();
    const offset = date.getTimezoneOffset();
    date = new Date(date.getTime() - (offset * 60 * 1000));

    /**
     * sets the date of today as last valid date for a birthdate, including the timezone
     * format: yyyy-mm-dd
     */
    this.maxBirthdate = date.toISOString().split('T')[0];

    if (this.isLinkedCustomer()) {
      // existing customer (linked via customerId); backend uses the stored person/address,
      // so the personal-data fields are hidden and excluded from validation.
      this.form.controls.person.disable();
      this.form.controls.address.disable();
      return;
    }

    this.patchUrlParamsToForm();
  }

  isLinkedCustomer(): boolean {
    return !!this.urlParamProviderService.getQueryData()?.customerId;
  }

  private patchUrlParamsToForm(): void {
    if (this.urlParamProviderService.getQueryData()) {
      const personCtrl = this.form.controls.person.controls;

      this.patchSingleParamToFormControl(this.urlParamProviderService.getQueryData()?.firstname, personCtrl.firstname);
      this.patchSingleParamToFormControl(this.urlParamProviderService.getQueryData()?.lastname, personCtrl.lastname);
      this.patchSingleParamToFormControl(this.urlParamProviderService.getQueryData()?.email, personCtrl.email);
      this.patchSingleParamToFormControl(this.urlParamProviderService.getQueryData()?.phone, personCtrl.phone);
      this.patchSingleParamToFormControl(this.urlParamProviderService.getQueryData()?.gender, personCtrl.gender);
      this.patchSingleParamToFormControl(this.urlParamProviderService.getQueryData()?.birthDate, personCtrl.birthdate);
      this.patchSingleParamToFormControl(this.urlParamProviderService.getQueryData()?.phone, personCtrl.phone);
    }
  }

  private patchSingleParamToFormControl(paramValue: any, ctrl: FormControl): void {
    if (!ctrl.value && paramValue) {
      ctrl.setValue(paramValue);
    }
  }

  sendCustomerData(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }
    this.sendingRequest = true;
    this.submitted.emit();
  }

  canShowContactData(): boolean {
    return this.form.controls.person.controls.firstname.value !== null
      && this.form.controls.person.controls.lastname.value !== null
      && this.form.controls.person.controls.birthdate.value !== null;
  }

  canShowAddress(): boolean {
    return this.canShowContactData()
      && this.form.controls.person.controls.phone.value !== null
      && this.form.controls.person.controls.email.value !== null;
  }

  canShowLegalChecks(): boolean {
    return this.isLinkedCustomer() || (this.canShowAddress() && this.form.controls.address.valid);
  }

  canShowAdditionalInformation(): boolean {
    const legalCtrls = this.form.controls.legalChecks.controls;
    return legalCtrls.isContactConsent.value === true
      && legalCtrls.isGdprAccepted.value === true;
  }

  canSubmit(): boolean {
    const legalForm = this.form.controls.legalChecks;

    return legalForm.controls.isContactConsent.value === true
      && legalForm.controls.isGdprAccepted.value === true
      && (this.isLinkedCustomer() || this.form.controls.address.valid);
  }

}

